import React, { MutableRefObject, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import throttle from 'lodash/throttle';

import {
    NewBuildingCard,
    NewBuildingWhitelabelEnum
} from '@search/graphql-typings';

import classname from '@search/classname/src';
import { DATA_GTM } from '@search/vtbeco-frontend-core/domain/newbuilding/libs/constants';
import { formatRange } from '@search/helpers/src/formatNumber';
import { BadgeBox } from '@search/vtbeco-frontend-core/view/common/components/BadgeBox';
import { NewBuildingClassBadge } from '@search/vtbeco-frontend-core/view/common/components/Badge/NewBuildingClassBadge';
import CustomTypography from '@search/vtbeco-frontend-core/domain/newbuilding/components/common/CustomTypography';
import Image from '@search/vtbeco-frontend-core/view/common/components/Image';
import { CallbackModal } from '@search/vtbeco-frontend-core/view/common/components/CallbackModal';
import { QRCodePhoneButton } from '@search/vtbeco-frontend-core/view/common/components/QRCodePhoneButton';
import { getDeveloperBannerGtmData } from '@search/vtbeco-frontend-core/domain/newbuilding/developer-promo/analytics';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { isM2ProUser } from '@search/auth/src/helpers/isM2ProUser';

import MediaKitFixedSizeBanner from '@search/vtbeco-frontend-core/domain/newbuilding/components/common/MediaKitFixedSizeBanner';
import { FixedSizeBanner } from '@vtblife/media-kit/dist/banners/fixed-size-banner/fixed-size-banner';

import type { NewBuildingCardQueryResponse } from '../../../../../../client/common/components/NewBuildingCard';
import type { IOnChangeGlobalContext } from '../../../../../../client/common/components/GlobalContext';
import { GlobalContext, OnChangeGlobalContext } from '../../../../../../client/common/components/GlobalContext';
import { SvgSpriteIcon } from '../../../../../../client/common/components/SvgSpriteIcon';
import Sticky from '../../../../../../client/common/components/Sticky';
import FavoritesButton from '../../../../../../client/common/components/FavoritesButton';
import { defineCallbackEmailSender } from '../../../../../../client/common/libs/defineEmailSender';
import { useFavoriteNewBuildingButton } from '../../../../../../client/common/components/FavoritesButton/useFavoriteNewBuildingButton';
import { usePhoneButtonContext } from '../../../../../../client/common/components/NewBuildingCard/PhoneButtonStateProvider';
import ChatButton from '../../../../../../client/common/components/ChatButton';
import { ChatStateProvider } from '../../../../../../client/common/components/NewBuildingCard/ChatStateProvider';
import RewardAmountIcon
    from '../../../../../../client/common/components/NewBuildingCard/NewBuildingCardOfferSearch/RewardAmountIcon';
import { M2ProOrderButton } from '../../../../../../client/common/components/M2ProOrderButton';
import { M2ProAttachedFilesButton } from '../../../../../../client/common/components/M2ProAttachedFilesButton';
import { useM2ProFiles } from '../../../../../../client/common/hooks/useM2ProFiles';
import { checkRegionIsPromoSamolet } from '../../../../../../client/common/libs/samolet-regions';
import { getBannerConfig, BannerType } from '../../../../../../client/common/libs/banner-config';

import type { INewBuildingCardDeveloper } from '../NewBuildingCardDeveloper';
import NewBuildingCardDeveloper from '../NewBuildingCardDeveloper';

import './styles.css';

export interface INewBuildingCardRightBlockProps {
    developer?: NewBuildingCardQueryResponse['newBuilding']['developerCards'][0]['developer'];
    newBuildingId: NewBuildingCard['id'];
    newBuildingName: NewBuildingCard['name'];
    newBuildingType: NewBuildingCard['type'];
    newBuildingTitle: NewBuildingCard['title']['nominative'];
    newBuildingTitleAccusative?: NewBuildingCard['title']['accusative'];
    newBuildingAddress: NewBuildingCard['address'];
    priceMin: NewBuildingCard['priceMin'];
    priceMax: NewBuildingCard['priceMax'];
    pricePerM2Min: NewBuildingCard['pricePerM2Min'];
    pricePerM2Max: NewBuildingCard['pricePerM2Max'];
    buildingClass: NewBuildingCardQueryResponse['newBuilding']['buildingClass'];
    buildingStatus: NewBuildingCardQueryResponse['newBuilding']['buildingStatus'];
    areaRange: NewBuildingCard['formatAreaRange'];
    isFavorite: NewBuildingCard['isFavorite'];
    developerLink?: string;
    developerNames: string;
    hasApartmentsOnly: boolean;
    isSoldOut: boolean;
    isSoon: boolean;
    stickyRef?: MutableRefObject<HTMLDivElement>;
    isMskMO?: boolean;
    isSPbLO?: boolean;
    isSpecProjectRegion?: boolean;
    hasChat?: boolean;
    developerPromoBanner?: NewBuildingCard['developerPromoBanner'];
    m2Pro?: NewBuildingCard['m2Pro'];
    staticPath?: string;
    hasApartments?: boolean;
}

const cn = classname.bind(null, 'NewBuildingCardRightBlock');

// eslint-disable-next-line complexity
const NewBuildingCardRightBlock: React.FunctionComponent<INewBuildingCardRightBlockProps> = ({
    developerLink,
    developer,
    developerNames,
    newBuildingId,
    isMskMO,
    isSPbLO,
    isSpecProjectRegion,
    hasChat,
    newBuildingName,
    newBuildingType,
    newBuildingTitle,
    newBuildingTitleAccusative,
    newBuildingAddress,
    buildingClass,
    buildingStatus,
    priceMin,
    priceMax,
    pricePerM2Min,
    pricePerM2Max,
    areaRange,
    hasApartmentsOnly,
    isSoldOut,
    isSoon,
    stickyRef,
    developerPromoBanner,
    isFavorite,
    m2Pro
}) => {
    const onChangeGlobalContext = useContext<IOnChangeGlobalContext>(OnChangeGlobalContext);
    const { instanceConfig: { hasFavoritesButton, hasDeveloperPromo, searchIndex } } = useContext(GlobalContext);

    const { auth } = useM2AuthContext();

    const rewardAmount = null;
    const { prepayment, cooperationTerms, nbPresentation } = m2Pro ?? {};
    const hasM2Pro = isM2ProUser(auth.user?.currentRoleType) && Boolean(rewardAmount);

    const m2ProFiles = useM2ProFiles({ cooperationTerms, nbPresentation });

    const [ isStickySectionVisible, showStickySection ] = useState<boolean>(false);
    const handleScroll = useCallback(() => {
        const { bottom = 1 } = stickyRef?.current?.getBoundingClientRect() ?? {};

        showStickySection(bottom <= 0);
    }, [ stickyRef, showStickySection ]);

    useEffect(() => {
        const throttledHandleScroll = throttle(handleScroll, 100);

        document.addEventListener('scroll', throttledHandleScroll);

        return () => {
            document.removeEventListener('scroll', throttledHandleScroll);
        };
    }, [ handleScroll ]);

    const priceRange = formatRange(priceMin, priceMax, {
        billionsAsString: true,
        thousandsAsNumber: true
    });
    const pricePerM2Range = formatRange(pricePerM2Min, pricePerM2Max, {
        billionsAsString: true,
        thousandsAsNumber: true
    });

    const developerProps = {
        developerLink,
        developerTitle: developer?.title,
        developerImage: developer?.icon,
        developerNames,
        isMskMO,
        isSPbLO,
        hasChat,
        newBuildingId,
        newBuildingTitle,
        newBuildingTitleAccusative,
        newBuildingAddress,
        dataGTM: DATA_GTM.CARD_PHONE_1,
        isSpecProjectRegion,
        hasM2Pro,
        m2ProFiles
    };

    const favoritesButtonProps = useFavoriteNewBuildingButton({
        newBuildingId: String(newBuildingId),
        isFavorite
    });

    const developerPromoGtms = useMemo(() => {
        const gtmClickData = getDeveloperBannerGtmData(developerPromoBanner?.developerName, {
            action: developerPromoBanner?.developerName === 'mortgage' ? 'open' : 'click',
            section: 'banner_right',
            pageType: 'card'
        });

        return {
            gtmClickData,
            gtmShowData: getDeveloperBannerGtmData(developerPromoBanner?.developerName, {
                action: 'view',
                section: 'banner_right',
                pageType: 'card'
            })
        };
    }, [ developerPromoBanner ]);

    const imageBanner = hasDeveloperPromo && developerPromoBanner && onChangeGlobalContext.linkBuilder.external ? (
        <MediaKitFixedSizeBanner
            gtmClickData={developerPromoGtms.gtmClickData}
            gtmShowData={developerPromoGtms.gtmShowData}
            dataGtm={DATA_GTM.CARD_SQUARE_BANNER}
        />
    ) : null;

    const isDefaultWhiteLabel = searchIndex === NewBuildingWhitelabelEnum.Default;
    const isSamoletPromoRegion = checkRegionIsPromoSamolet(undefined);
    const banner = getBannerConfig(BannerType.NB_CARD_RIGHT_DESKTOP);

    return (
        <>
            <div
                ref={stickyRef}
                className={cn('staticSection')}
            >
                <BadgeBox
                    position='built-in'
                    className={cn('badgeBox')}
                >
                    {buildingClass && (
                        <NewBuildingClassBadge
                            size='m'
                            level='secondary-alone'
                            newBuildingClass={buildingClass.value}
                        >
                            {buildingClass.fullLabel}
                        </NewBuildingClassBadge>
                    )}
                </BadgeBox>
                {
                    hasFavoritesButton ? (
                        <FavoritesButton
                            {...favoritesButtonProps}
                            className={cn('favorites-btn')}
                            inCircle
                        />
                    ) : null
                }
                {priceRange && (
                    <CustomTypography
                        level='h3'
                        className={cn('priceRange')}
                    >
                        {`${priceRange} ₽`}
                    </CustomTypography>
                )}
                {hasM2Pro && (
                    <div className={cn('m2Pro')}>
                        {rewardAmount && (
                            <RewardAmountIcon amount={rewardAmount} />
                        )}
                        {prepayment && (
                            <SvgSpriteIcon
                                name='M2ProPrepayment'
                                width={24}
                                height={24}
                                className={cn('m2ProPrepayment')}
                            />
                        )}
                    </div>
                )}
                {pricePerM2Range && (
                    <CustomTypography
                        color='secondary'
                        level='primary-alone'
                        className={cn('pricePerM2Range')}
                    >
                        {`${pricePerM2Range} ₽/м²`}
                    </CustomTypography>
                )}
                {(priceRange || pricePerM2Range) && (
                    <div className={cn('separatorLine')} />
                )}
                {buildingStatus?.length && (
                    <CommissioningDates buildingStatus={buildingStatus} />
                )}
                {areaRange || isSoldOut || isSoon ? (
                    <AreaRange
                        areaRange={areaRange}
                        hasApartmentsOnly={hasApartmentsOnly}
                        isSoldOut={isSoldOut}
                        isSoon={isSoon}
                    />
                ) : null}
                <Developer {...developerProps} />
            </div>
            {imageBanner && (
                <div className={cn('imageBanner', { hidden: isStickySectionVisible })}>
                    {imageBanner}
                </div>
            )}
            { isDefaultWhiteLabel && isSamoletPromoRegion && (
                <div className={cn('imageBanner', { hidden: isStickySectionVisible })}>
                    <FixedSizeBanner
                        placementId={banner.placementId}
                        group={banner.group}
                    />
                </div>
            ) }
            <Sticky
                top={20}
                visible={isStickySectionVisible}
                className={cn('stickySection')}
            >
                <NewBuildingCardDeveloper
                    {...developerProps}
                    newBuildingId={newBuildingId}
                    newBuildingName={newBuildingName}
                    newBuildingType={newBuildingType}
                    favoritesButtonProps={favoritesButtonProps}
                    m2ProFiles={m2ProFiles}
                />
                <div className={cn('imageBanner')}>
                    {imageBanner}
                </div>
                { isDefaultWhiteLabel && isSamoletPromoRegion && (
                    <div className={cn('imageBanner')}>
                        <FixedSizeBanner
                            placementId={banner.placementId}
                            group={banner.group}
                        />
                    </div>
                ) }
            </Sticky>
        </>
    );
};

export default React.memo(NewBuildingCardRightBlock);

const CommissioningDates = ({ buildingStatus }: { buildingStatus: NewBuildingCard['buildingStatus'] }) => (
    <div className={cn('commissioningDates')}>
        <CustomTypography
            level='primary-alone'
            color='secondary'
        >
            Срок сдачи
        </CustomTypography>
        {buildingStatus!.map((status, idx) => (
            <CustomTypography
                key={idx}
                bold
                level='primary-alone'
                className={cn('buildingStatus')}
            >
                {status.title}
            </CustomTypography>
        ))}
    </div>
);

const AreaRange = ({
    areaRange,
    hasApartmentsOnly,
    isSoldOut,
    isSoon
}: {
    areaRange: NewBuildingCard['formatAreaRange'];
    hasApartmentsOnly: boolean;
    isSoldOut: boolean;
    isSoon: boolean;
}) => (
    <div className={cn('areaRange')}>
        <CustomTypography
            level='primary-alone'
            color='secondary'
        >
            {`${hasApartmentsOnly ? 'Апартаменты' : 'Квартиры'} в продаже`}
        </CustomTypography>
        <CustomTypography
            bold
            level='primary-alone'
            className={cn('areaRangeValue')}
        >
            {
                // eslint-disable-next-line no-nested-ternary
                isSoldOut ? 'Всё продано' :
                    isSoon ? 'Скоро в продаже' :
                        areaRange
            }
        </CustomTypography>
    </div>
);

// eslint-disable-next-line complexity
const Developer = ({
    developerLink,
    developerTitle,
    developerImage,
    developerNames,
    dataGTM,
    isMskMO,
    isSPbLO,
    isSpecProjectRegion,
    hasChat,
    newBuildingId,
    newBuildingTitle,
    newBuildingTitleAccusative,
    newBuildingAddress,
    hasM2Pro,
    m2ProFiles
}: Omit<INewBuildingCardDeveloper, 'newBuildingName' | 'newBuildingType'>) => {
    const { instanceConfig: { calltouchUnitId } } = useContext(GlobalContext);
    const { buttonsData, firstDeveloperId, handleClick, handleStatEventPush } = usePhoneButtonContext();
    const isPhoneRegion = isMskMO || isSPbLO || isSpecProjectRegion;
    const showM2ProOderButton = Boolean(newBuildingId && newBuildingTitle && developerTitle);

    return (
        <>
            <div className={cn('developer')}>
                <div>
                    <CustomTypography
                        level='primary-alone'
                        color='secondary'
                    >
                        Застройщик
                    </CustomTypography>
                    <CustomTypography
                        bold
                        tag='a'
                        level='primary-alone'
                        color='primary'
                        target='_blank'
                        href={developerLink}
                        className={cn('developerName')}
                    >
                        {developerTitle}
                    </CustomTypography>
                </div>
                <div className={cn('developerImage')}>
                    {developerImage?.origin ? (
                        <Image
                            type='contain'
                            width={40}
                            height={40}
                            url={developerImage.origin}
                        />
                    ) : (
                        <SvgSpriteIcon
                            width={40}
                            height={40}
                            name='CustomDeveloper'
                        />
                    )}
                </div>
            </div>
            {firstDeveloperId && ! hasM2Pro && (
                <QRCodePhoneButton
                    href={buttonsData[firstDeveloperId].href}
                    onClick={() => handleClick(firstDeveloperId)}
                    qrCodeValue={buttonsData[firstDeveloperId].qrCodeValue}
                    className={cn('showPhone')}
                    gtm={dataGTM}
                    loading={buttonsData[firstDeveloperId].isLoading}
                    buttonVariant={isPhoneRegion ? 'primary' : 'secondary'}
                >
                    {buttonsData[firstDeveloperId].text}
                </QRCodePhoneButton>
            )}
            {(isMskMO || isSPbLO) && ! hasM2Pro && (
                <CallbackModal
                    newBuildingName={newBuildingTitle}
                    address={newBuildingAddress}
                    emailSender={defineCallbackEmailSender(isMskMO, isSPbLO)}
                    developerName={developerNames}
                    gtmSendClick={DATA_GTM.CARD_CALL_BACK_SEND}
                    onSendEventPush={handleStatEventPush}
                    gtmShowModal={DATA_GTM.CARD_CALL_BACK}
                    btnWrapperClass={cn('callBackButton')}
                    callbackUnitId={calltouchUnitId[isSPbLO ? 'Spb' : 'Msk']}
                />
            )}
            {
                hasChat && ! hasM2Pro ? (
                    <ChatStateProvider initialClientMessage={`Интересует ${newBuildingTitleAccusative || newBuildingTitle}`}>
                        <ChatButton
                            className={cn('openChat')}
                            size='m'
                            fullWidth
                            hasText
                        />
                    </ChatStateProvider>
                ) : null
            }
            {
                hasM2Pro ? (
                    <>
                        {showM2ProOderButton && (
                            <div className={cn('m2ProButton')}>
                                <M2ProOrderButton
                                    newBuildingId={String(newBuildingId)}
                                    fullWidth
                                    eventData={{
                                        page_type: 'card',
                                        position: 'top'
                                    }}
                                />
                            </div>
                        )}
                        <ChatStateProvider initialClientMessage={`Интересует ${newBuildingTitleAccusative || newBuildingTitle}`}>
                            <ChatButton
                                className={cn('openChat')}
                                size='m'
                                fullWidth
                                hasText
                                hasIcon={false}
                                variant={showM2ProOderButton ? 'secondary' : 'primary'}
                            />
                        </ChatStateProvider>
                        {m2ProFiles.length > 0 && (
                            <div className={cn('m2ProButton')}>
                                <M2ProAttachedFilesButton
                                    view='button'
                                    variant='transparent'
                                    files={m2ProFiles}
                                    eventData={{
                                        page_type: 'card',
                                        position: 'top'
                                    }}
                                />
                            </div>
                        )}
                    </>
                ) : null
            }
        </>
    );
};
